/* eslint-disable camelcase */
import { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { isArray } from 'lodash';
import {
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  SETTINGS_ALL_QUERY_KEY,
  NEW_SETTINGS_QUERY_KEY_TOGGLE,
  NEW_SETTINGS_QUERY_ALL,
  NEW_SETTINGS_QUERY_ALL_AUTH,
} from '../sharedQueries';

export const useCurrency = (currencyCode) => {
  const { data: enabledCurrencies, isLoading: isLoadingCurrencies } = useQuery([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);
  if (!isLoadingCurrencies && enabledCurrencies) {
    if (enabledCurrencies) {
      return enabledCurrencies[currencyCode?.toLowerCase()];
    }
  }

  return null;
};

const castingSettingValue = (dataType, value) => {
  let result;
  switch (dataType) {
    case 'BOOLEAN':
      result = value === 'true';
      break;
    case 'INTEGER':
      result = parseInt(value, 10);
      break;
    case 'DOUBLE':
      result = parseFloat(value);
      break;
    default:
      result = value;
  }
  return result;
};

const extractSettings = (mainObj, category, option, many = false) => {
  const result = {
    value: null,
    multi: {},
  };
  if (mainObj && Object.keys(mainObj).length > 0) {
    if (mainObj[category] && !many) {
      if (category === 'allowed_signup_countries') {
        result.value = mainObj[category];
      } else {
        const obj = mainObj[category]?.find((item) => item.option_name === option);
        result.value = castingSettingValue(obj?.data_type, obj?.option_value);
      }
    } else {
      const resultarr = mainObj[category]?.filter((item) => option?.includes(item?.option_name));
      resultarr?.forEach((setting) => {
        const optionName = setting?.option_name;
        result.multi[optionName] = castingSettingValue(setting?.data_type, setting?.option_value);
      });
    }
  }
  return result;
};

export const useSetting = (category, settingName, all = false, auth = true) => {
  const isMany = isArray(settingName);

  const { data: settings, isLoading: isLoadingAllowNewSettingEndoint } = useQuery([
    NEW_SETTINGS_QUERY_KEY_TOGGLE,
    ['feature_allow_new_settings_end_point'],
  ]);

  const { value: allowNewSettingsEndpoint } = extractSettings(
    settings,
    'app_features',
    'feature_allow_new_settings_end_point'
  );

  const {
    data: settingsData,
    isLoading,
    refetch: refetchAllSettins,
  } = useQuery(SETTINGS_ALL_QUERY_KEY, {
    enabled: false,
  });

  const {
    data: newEndpointSettingsData,
    refetch: refetchNewEndpointSettingsData,
    isLoading: isLoadingNewEndpointSettings,
  } = useQuery(auth ? NEW_SETTINGS_QUERY_ALL : NEW_SETTINGS_QUERY_ALL_AUTH, {
    enabled: false,
  });

  useEffect(() => {
    if (!isLoadingAllowNewSettingEndoint && !allowNewSettingsEndpoint) {
      refetchAllSettins();
    } else if (!isLoadingAllowNewSettingEndoint && allowNewSettingsEndpoint) {
      refetchNewEndpointSettingsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowNewSettingsEndpoint, isLoadingAllowNewSettingEndoint]);

  const newSettingsValue = useMemo(() => {
    if (
      newEndpointSettingsData &&
      Object.keys(newEndpointSettingsData).length > 0 &&
      !isLoadingNewEndpointSettings
    ) {
      if (isMany) {
        return extractSettings(newEndpointSettingsData, category, settingName, true)?.multi;
      }
      return extractSettings(newEndpointSettingsData, category, settingName)?.value;
    }

    return null;
  }, [newEndpointSettingsData, isLoadingNewEndpointSettings, isMany, category, settingName]);

  const value = useMemo(() => {
    if (settingsData && settingsData.settings) {
      const categorySettings = settingsData.settings[category];

      if (all) return categorySettings;

      if (typeof settingName === 'object') {
        const result = {};
        categorySettings?.forEach((item) => {
          if (settingName.includes(item.option_name)) {
            const { option_value, data_type, option_name } = item;
            result[option_name] = castingSettingValue(data_type, option_value);
          }
        });
        return result;
      }
      let optionValue = '';
      const settingItem = categorySettings?.find((item) => item.option_name === settingName);
      if (!settingItem) {
        return '';
      }

      const { option_value, data_type } = settingItem;

      if (option_value && data_type) {
        optionValue = castingSettingValue(data_type, option_value);
      }
      return optionValue;
    }
    return null;
  }, [all, category, settingName, settingsData]);

  if (allowNewSettingsEndpoint) {
    return [newSettingsValue, isLoadingNewEndpointSettings];
  }

  return [value, isLoading];
};
